import { CreditCard as CreditCardIcon, TriangleAlert } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Notice } from "@/components/ui/notice"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumeric,
  TableRow,
} from "@/components/ui/table"
import { InfoTip } from "@/components/ui/tooltip"
import {
  type AppliedRule,
  type ListMembership,
  type TransactionCard,
  TWIN_ID_PREFIX,
} from "@/lib/api"
import {
  EMPTY,
  formatAmount,
  formatDateFull,
  formatInt,
  formatScore,
  formatText,
  scoreDirection,
} from "@/lib/format"
import { cn } from "@/lib/utils"

import {
  EMAIL_PROFILES,
  field,
  flagOf,
  IP_FLAGS,
  numeric,
  PHONE_PROFILES,
  profiles,
} from "./card-data"
import { DataField, Field, Fields, Flag, MockPanel, Panel, ProfileChips } from "./card-ui"
import { SeonPanel } from "./seon-panel"

/** Тон бейджа/точки по исходу скоринга — те же три цвета, что в списке. */
type Tone = "approve" | "decline" | "review" | "neutral"

function stateTone(state: string | null | undefined): Tone {
  switch ((state ?? "").toUpperCase()) {
    case "APPROVE":
      return "approve"
    case "DECLINE":
      return "decline"
    case "REVIEW":
      return "review"
    default:
      return "neutral"
  }
}

/**
 * Тон → класс цвета текста. Литералами, а не сборкой `text-${tone}`: Tailwind
 * ищет классы по исходнику как есть, и составленное в рантайме имя в CSS не
 * попадёт — цвет молча пропал бы.
 */
const TONE_TEXT: Record<Tone, string> = {
  approve: "text-approve",
  decline: "text-decline",
  review: "text-review",
  neutral: "text-fg",
}

/**
 * Вкладка Summary — блоки §5 AMEND-10 в порядке образца.
 *
 * Всё живое приходит из `/api/transactions/{id}` — с AMEND-12 §6 сюда входит и
 * блок List status: он отвечает из нашей таблицы списков, а не из образца.
 * Занятые, но нереализованные места (Associated alerts, Movement of funds,
 * Session analysis) стоят заглушками и говорят об этом прямо. Того, что владелица вынесла из
 * объёма (AI summary, eKYC, AML, Notes, карта и фото адреса), здесь нет вовсе —
 * не пустой рамкой, а отсутствием.
 */
export function Summary({ card }: { card: TransactionCard }) {
  const { core, email, phone, session, custom_fields: custom, totals } = card
  // Блок SEON — только у строк двойника (AMEND-59 шаг 2): у строки выгрузки
  // решение и так от SEON, сравнивать её не с чем.
  const id = field(core, "id")
  const twinId = id?.startsWith(TWIN_ID_PREFIX) ? id : null

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <TransactionSummary core={core} email={email} />
      <FraudScore core={core} />
      {twinId && <SeonPanel twinId={twinId} />}
      {/* С блоком SEON Identity встаёт на всю ширину: иначе она осталась бы
          одна в своей строке рядом с пустой клеткой, а правила ниже и так
          во всю ширину. */}
      <Identity
        core={core}
        email={email}
        totals={totals}
        className={twinId ? "lg:col-span-2" : undefined}
      />
      <AppliedRules rules={card.applied_rules} />
      {/* Сразу под правилами: строка `B…` говорит «чёрный список», а что
          именно в списке у этой транзакции — здесь, фактом из нашей базы. */}
      <ListStatus lists={card.lists} />
      <Addresses core={core} />
      <IpInformation core={core} />
      <EmailInformation email={email} />
      <PhoneInformation phone={phone} />
      <DevicesOs session={session} />
      <CreditCard core={core} />
      <OrderDetails core={core} />
      <CustomFields custom={custom} />

      {/* Заняты на образце, за ними нет данных в этой сборке. */}
      <MockPanel
        title="Associated alerts & cases"
        reason="Связанные алерты и кейсы не реализованы в этой сборке."
      />
      <MockPanel
        title="Movement of funds"
        reason="Граф движения средств не реализован в этой сборке."
      />
      <MockPanel
        title="Session analysis"
        reason="Разбор сессии не реализован в этой сборке."
      />

      <CalculationTime core={core} />
    </div>
  )
}

// ── 1. Transaction summary ───────────────────────────────────────────────────

function TransactionSummary({
  core,
  email,
}: {
  core: TransactionCard["core"]
  email: TransactionCard["email"]
}) {
  const state = field(core, "state")
  const tags = field(core, "tags")
  const chips = tags ? tags.split(",").map((tag) => tag.trim()).filter(Boolean) : []

  return (
    <Panel
      title="Transaction summary"
      className="lg:col-span-2"
      right={state ? <Badge tone={stateTone(state)}>{state}</Badge> : undefined}
    >
      <Fields cols={3}>
        <DataField source={core} k="transaction_id" label="Transaction ID" />
        <DataField source={core} k="id" label="SEON ID" />
        <DataField source={core} k="user_id" label="User ID" />
        <DataField source={email} k="email_email" label="Email" />
        <Field label="Amount" value={formatAmount(numeric(core, "transaction_amount_eur"), "EUR")} />
        <DataField source={core} k="date" label="Date" />
        <DataField source={core} k="transaction_status" label="Transaction status" />
        <DataField source={core} k="assignee" label="Assigned to" />
        {/* `label` пуст во всех 221 542 строках выгрузки — поле честно даёт «—». */}
        <DataField source={core} k="label" label="Label" />
      </Fields>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="stencil-label text-fg-subtle">Tags</span>
        {chips.length === 0 ? (
          <span className="text-xs text-fg-subtle">{EMPTY}</span>
        ) : (
          chips.map((tag) => (
            <span
              key={tag}
              className="rounded-sm border border-line bg-surface-sunken px-1.5 py-px text-xs leading-4 text-fg-muted"
            >
              {tag}
            </span>
          ))
        )}
      </div>
    </Panel>
  )
}

// ── 2. Fraud score ───────────────────────────────────────────────────────────

function FraudScore({ core }: { core: TransactionCard["core"] }) {
  const score = formatScore(numeric(core, "fraud_score"))
  const state = field(core, "state")
  return (
    <Panel title="Fraud score">
      <div className="flex items-end justify-between gap-4">
        <span className={cn("text-4xl font-semibold leading-none", TONE_TEXT[stateTone(state)])}>
          {score}
        </span>
        {state ? <Badge tone={stateTone(state)}>{state}</Badge> : null}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-fg-subtle">
        Сумма вкладов сработавших правил ниже. Значение и исход берутся из выгрузки — здесь
        не пересчитываются.
      </p>
    </Panel>
  )
}

// ── 3. Applied rules — самая ценная часть карточки ───────────────────────────

function AppliedRules({ rules }: { rules: AppliedRule[] }) {
  return (
    <Panel
      title="Applied rules"
      className="lg:col-span-2"
      right={
        <span className="text-xs text-fg-subtle">
          {formatInt(rules.length)} · highest output
        </span>
      }
    >
      {rules.length === 0 ? (
        <p className="text-xs text-fg-muted">
          Ни одно правило не сработало на этой транзакции — вклад в счёт нулевой.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Rule</TableHead>
                {/*
                 * На узком экране фиксированные 112 + 96 px съедали две трети
                 * строки: в колонке Rule оставалось ~90 px, и подпись правила с
                 * пояснением про обрыв по чёрному списку рассыпалась по одному
                 * слову в строку. До `sm` колонки ужимаются под своё содержимое
                 * (номер правила, `+5`), с `sm` и выше ширины прежние. Номер
                 * примером не пишем: страж из `test_transactions_api` ищет
                 * литерал `B<цифры>` по всему исходнику, не разбирая, где
                 * комментарий, — и правильно делает, иначе вписанную руками
                 * табличку «правило → поле» на экране потом нечем заметить.
                 */}
                <TableHead className="w-16 sm:w-28">Rule ID</TableHead>
                <TableHead align="right" className="w-14 sm:w-24">Output</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => (
                <RuleRow key={rule.seon_id} rule={rule} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Panel>
  )
}

function RuleRow({ rule }: { rule: AppliedRule }) {
  // У правил `B*` нет строки в каталоге, а с ней и `action_details`: в колонке
  // Output стоял прочерк. Вместо него — номинал, который печатает SEON (`+5`),
  // отдельным полем: каталог остаётся тем, что пришло из выгрузки. Рядом, под
  // названием, честная строка — счёт решён обрывом, а не этой пятёркой
  // (AMEND-13 §3, замер на 6 619 строках). Реальный вес (`applied_output`,
  // AMEND-112) — первым: у 10008 с модификатором SEON номинал каталога `+5`
  // не сходился бы с итогом карточки.
  const output = rule.applied_output ?? rule.action_details ?? rule.blacklist_output
  const direction = scoreDirection(output)
  const tone =
    direction === "decrease"
      ? "text-approve"
      : direction === "increase"
        ? "text-decline"
        : "text-fg-muted"
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className="text-fg">
        {rule.name ? (
          formatText(rule.name)
        ) : rule.blacklist ? (
          // Правило семейства `B` — чёрный список: так сказала владелица, и так
          // написано на самом экране SEON. Названия у него нет ни в одной из
          // четырёх выгрузок каталога, поэтому подпись говорит ровно то, что
          // известно — семейство и номер. Чего правило касалось, здесь нет: это
          // из данных не следует (AMEND-12 §6).
          <span>
            чёрный список{" "}
            <span className="text-fg-subtle">
              (правило SEON <span>{formatText(rule.seon_id)}</span>,
              названия в каталоге нет)
            </span>
          </span>
        ) : (
          // Правила нет в каталоге аккаунта — строку всё равно показываем, иначе
          // счёт «сложился из ниоткуда». Об этом прямо: пустое имя не выдумываем.
          <span className="text-fg-subtle">не в каталоге</span>
        )}
        {rule.blacklist_note ? (
          <p className="mt-1 text-xs leading-snug text-fg-subtle">{rule.blacklist_note}</p>
        ) : null}
      </TableCell>
      <TableCell className="text-fg-muted">{formatText(rule.seon_id)}</TableCell>
      <TableNumeric className={cn("font-medium", tone)}>{formatText(output)}</TableNumeric>
    </TableRow>
  )
}

// ── 3a. List status — факт из нашей базы, рядом с правилами ──────────────────

/**
 * Какие поля этой транзакции сейчас лежат в чёрном списке **нашей** базы.
 *
 * Стоит рядом с Applied rules нарочно: строка правила `B…` говорит только про
 * семейство, а что именно у этой транзакции в списке — знает наша таблица, и
 * ответ на этот вопрос читается здесь. Связывать одно с другим экран не берётся:
 * какое поле проверяло правило SEON, из данных не следует (AMEND-12 §6).
 *
 * Восемь строк всегда на месте. Показывать одни совпадения было бы короче и
 * неверно: пропущенная строка читалась бы как «в списке этого нет», хотя в
 * половине случаев туда просто не смотрели.
 *
 * Значений списка на экране нет ни одного — только имя типа поля, ответ и
 * причина, по которой ответа нет. Сами значения — ПДн живых людей, и карточке
 * для своего вопроса они не нужны.
 */
function ListStatus({ lists }: { lists: ListMembership[] }) {
  const hits = lists.filter((item) => item.answer === true).length
  const silent = lists.filter((item) => item.answer === null).length
  return (
    <Panel
      title="List status"
      right={
        <span className="text-xs text-fg-subtle">
          blacklist · {formatInt(hits)} / {formatInt(lists.length)}
        </span>
      }
    >
      <div className="divide-y divide-line/60">
        {lists.map((item) => (
          <ListRow key={item.field_type} item={item} />
        ))}
      </div>
      {silent > 0 ? (
        <p className="mt-3 text-xs leading-relaxed text-fg-subtle">
          «нет ответа» — это не «нет»: сверять было нечего либо не с чем. Причина
          каждой такой строки — в подсказке рядом с ней.
        </p>
      ) : null}
    </Panel>
  )
}

function ListRow({ item }: { item: ListMembership }) {
  // Те же три цвета, что у Flag: попадание в ЧС — сигнал, ровное «нет» —
  // приглушено, «нет ответа» — самый тихий тон. Спорить за внимание с
  // настоящим попаданием спокойные состояния не должны.
  const dot =
    item.answer === true ? "bg-decline" : item.answer === false ? "bg-approve/60" : "bg-line-strong"
  const tone =
    item.answer === true ? "text-decline" : item.answer === false ? "text-fg-muted" : "text-fg-subtle"
  const text = item.answer === true ? "в списке" : item.answer === false ? "нет" : "нет ответа"
  const state = <span className={cn("text-xs font-medium", tone)}>{text}</span>
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="flex items-center gap-1.5">
        <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", dot)} />
        <span className="text-xs text-fg">{item.field_type}</span>
      </span>
      {item.reason ? (
        <InfoTip label={item.reason}>
          <span className="cursor-help border-b border-dotted border-line-strong">{state}</span>
        </InfoTip>
      ) : (
        state
      )}
    </div>
  )
}

// ── 4. Identity ──────────────────────────────────────────────────────────────

function Identity({
  core,
  email,
  totals,
  className,
}: {
  core: TransactionCard["core"]
  email: TransactionCard["email"]
  totals: TransactionCard["totals"]
  className?: string
}) {
  return (
    <Panel title="Identity" className={className}>
      <Fields>
        <DataField source={core} k="user_id" label="User ID" />
        <DataField source={core} k="user_fullname" label="Full name" />
        <DataField source={core} k="user_dob" label="Date of birth" />
        <DataField source={email} k="email_email" label="Email" />
        <DataField source={core} k="merchant_id" label="Merchant ID" />
        <Field label="Total amount" value={formatAmount(totals.total_amount_eur, "EUR")} />
        <Field label="Total transactions" value={formatInt(totals.transaction_count)} />
        <Field label="First seen" value={formatDateFull(totals.first_seen)} />
      </Fields>
    </Panel>
  )
}

// ── 5. Addresses (текстом, без карты и фото — §7) ────────────────────────────

function Addresses({ core }: { core: TransactionCard["core"] }) {
  return (
    <Panel title="Addresses">
      <div className="space-y-3">
        <div>
          <p className="stencil-label mb-1 text-fg-subtle">Billing</p>
          <Fields>
            <DataField source={core} k="billing_street" label="Street" />
            <DataField source={core} k="billing_city" label="City" />
            <DataField source={core} k="billing_zip" label="ZIP" />
            <DataField source={core} k="billing_country" label="Country" />
          </Fields>
        </div>
        <div className="border-t border-line pt-3">
          <p className="stencil-label mb-1 text-fg-subtle">IP geolocation</p>
          <Fields>
            <DataField source={core} k="ip_state_prov" label="State / province" />
            <DataField source={core} k="ip_country" label="Country" />
            <DataField source={core} k="ip_latitude" label="Latitude" />
            <DataField source={core} k="ip_longitude" label="Longitude" />
          </Fields>
        </div>
        <div className="border-t border-line pt-3">
          <Fields cols={1}>
            <DataField source={core} k="bin_country_code" label="Card country" />
          </Fields>
        </div>
      </div>
    </Panel>
  )
}

// ── 6. Email information ──────────────────────────────────────────────────────

function EmailInformation({ email }: { email: TransactionCard["email"] }) {
  if (!email) {
    return (
      <Panel title="Email information" className="lg:col-span-2">
        <p className="text-xs text-fg-muted">На транзакции нет данных о почте.</p>
      </Panel>
    )
  }
  return (
    <Panel
      title="Email information"
      className="lg:col-span-2"
      right={
        <span className="text-xs text-fg-subtle">
          network score {formatScore(numeric(email, "email_score"))}
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
        <div>
          <p className="stencil-label mb-1.5 text-fg-subtle">Email details</p>
          <Fields>
            <DataField source={email} k="email_minimum_age_months" label="Minimum age, months" />
            <DataField source={email} k="email_earliest_profile_date" label="Earliest profile" />
            <DataField source={email} k="email_email_randomness_score" label="Randomness score" />
          </Fields>
        </div>

        <div>
          <p className="stencil-label mb-1.5 text-fg-subtle">Digital footprint</p>
          <Fields>
            <DataField source={email} k="email_total_registration" label="Total registrations" />
          </Fields>
          <p className="stencil-label mb-1 mt-3 text-fg-subtle">Registered profiles</p>
          <ProfileChips items={profiles(email, EMAIL_PROFILES)} />
        </div>

        <div>
          <p className="stencil-label mb-1.5 text-fg-subtle">
            Fraudulent network transaction history
          </p>
          <Fields>
            <DataField source={email} k="email_customer_hits" label="Customer hits" />
            <DataField source={email} k="email_hits" label="Network hits" />
            <DataField
              source={email}
              k="email_fraudulent_decline_customer_hits"
              label="Decline · customer hits"
            />
            <DataField
              source={email}
              k="email_fraudulent_decline_hits"
              label="Decline · network hits"
            />
            <DataField source={email} k="email_first_seen" label="First seen" />
            <DataField source={email} k="email_last_seen" label="Last seen" />
          </Fields>
        </div>

        <div>
          <p className="stencil-label mb-1.5 text-fg-subtle">Data breaches</p>
          <Fields>
            <DataField source={email} k="email_number_of_breaches" label="Breaches" />
            <DataField source={email} k="email_first_breach" label="First breach" />
            <DataField source={email} k="email_last_breach" label="Last breach" />
          </Fields>
        </div>

        <div className="lg:col-span-2">
          <p className="stencil-label mb-1.5 text-fg-subtle">Email domain</p>
          <Fields cols={3}>
            <DataField source={email} k="email_domain_tld" label="TLD" />
            <DataField source={email} k="email_domain_registrar_name" label="Registrar" />
            <div className="min-w-0">
              <dt className="stencil-label text-fg-subtle">Type</dt>
              <dd className="mt-0.5 flex flex-col gap-0.5">
                <Flag label="Free" on={flagOf(email, "email_domain_free")} />
                <Flag label="Custom" on={flagOf(email, "email_domain_custom")} />
                <Flag label="Disposable" on={flagOf(email, "email_domain_disposable")} />
              </dd>
            </div>
          </Fields>
        </div>
      </div>
    </Panel>
  )
}

// ── 7. Phone information ──────────────────────────────────────────────────────

function PhoneInformation({ phone }: { phone: TransactionCard["phone"] }) {
  if (!phone) {
    return (
      <Panel title="Phone information" className="lg:col-span-2">
        <p className="text-xs text-fg-muted">На транзакции нет данных о телефоне.</p>
      </Panel>
    )
  }
  return (
    <Panel
      title="Phone information"
      className="lg:col-span-2"
      right={
        <span className="text-xs text-fg-subtle">
          score {formatScore(numeric(phone, "phone_score"))}
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
        <div>
          <p className="stencil-label mb-1.5 text-fg-subtle">Telecom details</p>
          <Fields>
            <DataField source={phone} k="phone_country" label="Country" />
            <DataField source={phone} k="phone_carrier" label="Carrier" />
            <DataField source={phone} k="phone_type" label="Type" />
            <DataField source={phone} k="phone_total_registration" label="Total registrations" />
          </Fields>
          <p className="stencil-label mb-1 mt-3 text-fg-subtle">Registered profiles</p>
          <ProfileChips items={profiles(phone, PHONE_PROFILES)} />
        </div>

        <div>
          <p className="stencil-label mb-1.5 text-fg-subtle">
            Fraudulent network transaction history
          </p>
          <Fields>
            <DataField source={phone} k="phone_customer_hits" label="Customer hits" />
            <DataField source={phone} k="phone_hits" label="Network hits" />
            <DataField
              source={phone}
              k="phone_fraudulent_decline_customer_hits"
              label="Decline · customer hits"
            />
            <DataField source={phone} k="phone_first_seen" label="First seen" />
          </Fields>
        </div>

        <details className="group lg:col-span-2">
          <summary className="stencil-label cursor-pointer select-none text-fg-subtle transition-colors hover:text-fg">
            Telco attributes
          </summary>
          <div className="mt-2">
            <Fields cols={3}>
              <DataField source={phone} k="phone_hlr_details_status" label="HLR status" />
              <DataField source={phone} k="phone_hlr_details_imsi" label="IMSI" mono />
              <DataField source={phone} k="phone_hlr_details_orig_carrier" label="Original carrier" />
              <DataField source={phone} k="phone_hlr_details_ported_carrier" label="Ported carrier" />
              <DataField source={phone} k="phone_hlr_details_roaming_carrier" label="Roaming carrier" />
              <DataField source={phone} k="phone_hlr_details_serving_msc" label="Serving MSC" mono />
              <DataField source={phone} k="phone_cnam_details_name" label="CNAM name" />
            </Fields>
          </div>
        </details>
      </div>
    </Panel>
  )
}

// ── 8. IP information ─────────────────────────────────────────────────────────

function IpInformation({ core }: { core: TransactionCard["core"] }) {
  return (
    <Panel
      title="IP information"
      right={
        <span className="text-xs text-fg-subtle">
          proxy {formatScore(numeric(core, "proxy_score"))}
        </span>
      }
    >
      <Fields>
        <DataField source={core} k="ip" label="IP address" />
        <DataField source={core} k="ip_type" label="Type" />
        <DataField source={core} k="ip_city" label="City" />
        <DataField source={core} k="ip_state_prov" label="State / province" />
        <DataField source={core} k="ip_country" label="Country" />
        <DataField source={core} k="ip_isp_name" label="ISP" />
      </Fields>
      <div className="mt-3 border-t border-line pt-3">
        <p className="stencil-label mb-1 text-fg-subtle">Flags</p>
        <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
          {IP_FLAGS.map(([key, label]) => (
            <Flag key={key} label={label} on={flagOf(core, key)} />
          ))}
        </div>
      </div>
    </Panel>
  )
}

// ── 9. Devices & OS ───────────────────────────────────────────────────────────

function DevicesOs({ session }: { session: TransactionCard["session"] }) {
  const missingReason = field(session, "missing_device_details_reason")
  // Ветки сессии нет вовсе, либо выгрузка сама объяснила, почему деталей нет —
  // оба случая это жёлтое предупреждение, а не пустые поля (docstring ручки).
  const warn = session === null ? "На транзакции нет данных о сессии." : missingReason

  return (
    <Panel title="Devices & OS">
      {warn ? (
        <Notice tone="warn" icon={TriangleAlert}>
          {warn}
        </Notice>
      ) : (
        <Fields>
          <DataField source={session} k="session_device_type" label="Device type" />
          <DataField source={session} k="session_os" label="OS" />
          <DataField source={session} k="session_browser" label="Browser" />
          <DataField source={session} k="session_browser_version" label="Browser version" />
          <DataField source={session} k="session_screen_resolution" label="Resolution" />
          <DataField source={session} k="session_timezone" label="Timezone" />
          <DataField source={session} k="session_battery_charging" label="Battery charging" />
          <DataField source={session} k="session_battery_level" label="Battery level" />
        </Fields>
      )}
    </Panel>
  )
}

// ── 10. Credit card (рисованная CSS-карта, без внешних картинок — §7) ─────────

function CreditCard({ core }: { core: TransactionCard["core"] }) {
  const bin = field(core, "card_bin")
  const last = field(core, "card_last")
  const brand = field(core, "card_issuer")
  const holder = field(core, "card_fullname")
  const expire = field(core, "card_expire")

  // Маска из того, что реально есть: BIN (первые цифры) и последние четыре.
  // Середину не выдумываем — точки, а не правдоподобные цифры.
  const masked = `${bin ?? "••••••"} •••••• ${last ?? "••••"}`

  return (
    <Panel title="Credit card">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex aspect-[1.6] w-full max-w-[15rem] shrink-0 flex-col justify-between rounded-lg border border-ink-line bg-ink p-3 text-ink-fg shadow-raised">
          <div className="flex items-center justify-between">
            <CreditCardIcon className="h-5 w-5 opacity-70" strokeWidth={1.75} />
            <span className="text-xs font-medium uppercase tracking-[0.04em] opacity-80">
              {brand ?? ""}
            </span>
          </div>
          <div className="font-mono text-sm tracking-[0.14em]">{masked}</div>
          <div className="flex items-end justify-between gap-2">
            <span className="truncate text-xs uppercase tracking-wide opacity-90">
              {holder ?? EMPTY}
            </span>
            <span className="shrink-0 text-xs opacity-90">{expire ?? EMPTY}</span>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <Fields>
            <DataField source={core} k="card_hash" label="Card hash" mono />
            <DataField source={core} k="bin_bank" label="Issuing bank" />
            <DataField source={core} k="bin_type" label="Type" />
            <DataField source={core} k="bin_level" label="Level" />
            <DataField source={core} k="bin_country_code" label="Country" />
            <DataField source={core} k="bin_valid" label="BIN valid" />
          </Fields>
        </div>
      </div>
    </Panel>
  )
}

// ── 11. Order details ─────────────────────────────────────────────────────────

function OrderDetails({ core }: { core: TransactionCard["core"] }) {
  const memo = field(core, "user_order_memo")
  return (
    <Panel title="Order details">
      {memo ? (
        <p className="whitespace-pre-wrap break-words text-xs text-fg">{memo}</p>
      ) : (
        <p className="text-xs text-fg-subtle">{EMPTY}</p>
      )}
    </Panel>
  )
}

// ── 12. Custom fields (разобранный JSON колонки) ─────────────────────────────

function CustomFields({ custom }: { custom: TransactionCard["custom_fields"] }) {
  const entries = custom ? Object.entries(custom) : []
  return (
    <Panel title="Custom fields">
      {entries.length === 0 ? (
        <p className="text-xs text-fg-subtle">{EMPTY}</p>
      ) : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {entries.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="stencil-label text-fg-subtle">{key}</dt>
              <dd className="break-words font-mono text-xs text-fg">{renderScalar(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </Panel>
  )
}

/** Скаляр custom-поля к строке; объект/массив — компактным JSON, не «[object]». */
function renderScalar(value: unknown): string {
  if (value === null || value === undefined) return EMPTY
  if (typeof value === "string") return value.trim() === "" ? EMPTY : value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return EMPTY
  }
}

// ── 13. Calculation time ──────────────────────────────────────────────────────

function CalculationTime({ core }: { core: TransactionCard["core"] }) {
  const value = field(core, "calculation_time")
  return (
    <Panel title="Calculation time" className="lg:col-span-2">
      <p className="text-xs text-fg">{value ? `${value} ms` : EMPTY}</p>
    </Panel>
  )
}
